// Utilidad para generar reportes Excel de planillas con formato (colores, negritas, merges).
// Usa xlsx-js-style (fork de SheetJS con soporte de estilos).
import * as XLSX from "xlsx-js-style";
import { computeRecordsHours, buildCCSSDayMap, horaNormal, horaExtra, aplicaJornada48 } from "@/lib/payroll";

// === Paleta VérticePro ===
const COLOR_PRIMARY = "274735";   // moss green (header titulo)
const COLOR_ACCENT  = "D96C4A";   // terracotta
const COLOR_HEADER  = "E6EFEA";   // verde claro para encabezados de columna
const COLOR_TOTAL   = "FAF2EE";   // beige claro para totales
const COLOR_TEXT    = "1A2421";

const fmtCRC = "#,##0.00";
const fmtHrs = "#,##0.00";

const styleTitle = {
  font: { name: "Calibri", sz: 16, bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_PRIMARY } },
  alignment: { horizontal: "left", vertical: "center" },
};
const styleSubtitle = {
  font: { name: "Calibri", sz: 12, bold: true, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_ACCENT } },
  alignment: { horizontal: "left", vertical: "center" },
};
const styleMetaInfo = {
  font: { name: "Calibri", sz: 10, italic: true, color: { rgb: COLOR_TEXT } },
  alignment: { horizontal: "left", vertical: "center" },
};
const styleHeader = {
  font: { name: "Calibri", sz: 10, bold: true, color: { rgb: COLOR_PRIMARY } },
  fill: { fgColor: { rgb: COLOR_HEADER } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: "B8C5BE" } },
    bottom: { style: "thin", color: { rgb: "B8C5BE" } },
    left: { style: "thin", color: { rgb: "B8C5BE" } },
    right: { style: "thin", color: { rgb: "B8C5BE" } },
  },
};
const styleCell = {
  font: { name: "Calibri", sz: 10, color: { rgb: COLOR_TEXT } },
  alignment: { vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: "EAE7E0" } },
    bottom: { style: "thin", color: { rgb: "EAE7E0" } },
    left: { style: "thin", color: { rgb: "EAE7E0" } },
    right: { style: "thin", color: { rgb: "EAE7E0" } },
  },
};
const styleCellNum = { ...styleCell, alignment: { horizontal: "right", vertical: "center" }, numFmt: fmtCRC };
const styleCellHrs = { ...styleCell, alignment: { horizontal: "right", vertical: "center" }, numFmt: fmtHrs };
const styleTotal = {
  font: { name: "Calibri", sz: 11, bold: true, color: { rgb: COLOR_PRIMARY } },
  fill: { fgColor: { rgb: COLOR_TOTAL } },
  alignment: { vertical: "center" },
  border: {
    top: { style: "medium", color: { rgb: COLOR_PRIMARY } },
    bottom: { style: "medium", color: { rgb: COLOR_PRIMARY } },
    left: { style: "thin", color: { rgb: "B8C5BE" } },
    right: { style: "thin", color: { rgb: "B8C5BE" } },
  },
};
const styleTotalNum = { ...styleTotal, alignment: { horizontal: "right", vertical: "center" }, numFmt: fmtCRC };

const COL_HEADERS = [
  "Empleado", "Identificación", "Puesto", "Departamento",
  "Hrs Ord", "Hrs Ord D", "Hrs Ord M", "Hrs Ord N", "Monto Hrs Ord",
  "Hrs Ext", "Hrs Ext D", "Hrs Ext M", "Hrs Ext N", "Monto Hrs Ext",
  "Día Libre días", "Día Libre monto",
  "Feriado días", "Feriado monto",
  "Incap. días", "Incap. pagos", "Incap. no pagos", "Incap. monto",
  "Vac. días", "Vac. monto",
  "Lic. días", "Lic. monto",
  "Ausente días",
  "Bonif. cant", "Bonif. monto",
  "Monto Ext (total)",
  "Salario Bruto", "CCSS", "Rebajos", "Salario Neto",
];

// Mapa de tipo de columna por índice (0-based) para aplicar formato.
// "t" texto, "n" número (colones), "h" horas
const COL_TYPES = [
  "t", "t", "t", "t",
  "h", "h", "h", "h", "n",
  "h", "h", "h", "h", "n",
  "n", "n",
  "n", "n",
  "n", "n", "n", "n",
  "n", "n",
  "n", "n",
  "n",
  "n", "n",
  "n",
  "n", "n", "n", "n",
];

const COL_WIDTHS = [
  28, 16, 22, 18,
  10, 10, 10, 10, 14,
  10, 10, 10, 10, 14,
  10, 14,
  10, 14,
  10, 10, 12, 14,
  10, 14,
  10, 14,
  10,
  10, 14,
  14,
  14, 12, 14, 14,
];

const RENTA_COL = COL_HEADERS.indexOf("CCSS") + 1;
const conColRenta = (arr, val, on) => (on ? [...arr.slice(0, RENTA_COL), val, ...arr.slice(RENTA_COL)] : arr);

function buildMainSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations, totals, MONTHS_ES, month, year, includeAguinaldo, conRenta = false }) {
  const aoa = [];
  // Cabecera (filas 0..3)
  aoa.push([empresaNombre]);
  aoa.push([tituloPeriodo]);
  aoa.push([`Fecha de reporte: ${fechaReporte}`]);
  aoa.push([]);
  // Encabezados (fila 4)
  const baseHeaders = conColRenta(COL_HEADERS, "Imp. Renta", conRenta);
  const headers = includeAguinaldo ? [...baseHeaders, "Aguinaldo Prov."] : [...baseHeaders];
  aoa.push(headers);

  // Filas de empleados
  for (const { emp, calc } of calculations) {
    const b = calc.breakdown || {};
    const row0 = [
      `${emp.nombre} ${emp.apellidos || ""}`.trim(),
      String(emp.cedula || ""),
      emp.puesto || "",
      emp.departamento || "",
      +calc.hrsOrd, +calc.byJornadaOrd.Diurna.hrs.toFixed(2), +calc.byJornadaOrd.Mixta.hrs.toFixed(2), +calc.byJornadaOrd.Nocturna.hrs.toFixed(2), calc.montoOrd,
      +calc.hrsExt, +calc.byJornadaExt.Diurna.hrs.toFixed(2), +calc.byJornadaExt.Mixta.hrs.toFixed(2), +calc.byJornadaExt.Nocturna.hrs.toFixed(2), calc.montoExt,
      b.diaLibre?.dias || 0, b.diaLibre?.monto || 0,
      b.feriado?.dias || 0, b.feriado?.monto || 0,
      b.incapacidadCCSS?.dias || 0, b.incapacidadCCSS?.diasPagos || 0, b.incapacidadCCSS?.diasNoPagos || 0, b.incapacidadCCSS?.monto || 0,
      b.vacaciones?.dias || 0, b.vacaciones?.monto || 0,
      b.licencia?.dias || 0, b.licencia?.monto || 0,
      b.ausente?.dias || 0,
      b.bonificaciones?.cantidad || 0, b.bonificaciones?.monto || 0,
      calc.montoExt,
      calc.totalBruto, calc.ccss, calc.totalRebajos, calc.neto,
    ];
    const row = conColRenta(row0, calc.renta || 0, conRenta);
    if (includeAguinaldo) row.push(Math.round(calc.totalBruto / 12));
    aoa.push(row);
  }

  // Fila de totales
  const totalRowIdx = aoa.length;
  const totalRow0 = [
    "TOTAL GENERAL", "", "", "",
    "", "", "", "", calculations.reduce((s, c) => s + c.calc.montoOrd, 0),
    "", "", "", "", calculations.reduce((s, c) => s + c.calc.montoExt, 0),
    "", calculations.reduce((s, c) => s + (c.calc.breakdown.diaLibre?.monto || 0), 0),
    "", calculations.reduce((s, c) => s + (c.calc.breakdown.feriado?.monto || 0), 0),
    "", "", "", calculations.reduce((s, c) => s + (c.calc.breakdown.incapacidadCCSS?.monto || 0), 0),
    "", calculations.reduce((s, c) => s + (c.calc.breakdown.vacaciones?.monto || 0), 0),
    "", calculations.reduce((s, c) => s + (c.calc.breakdown.licencia?.monto || 0), 0),
    calculations.reduce((s, c) => s + (c.calc.breakdown.ausente?.dias || 0), 0),
    "", calculations.reduce((s, c) => s + (c.calc.breakdown.bonificaciones?.monto || 0), 0),
    totals.extras,
    totals.bruto, totals.ccss, totals.rebajos, totals.neto,
  ];
  const totalRow = conColRenta(totalRow0, totals.renta || 0, conRenta).map(v => (typeof v === "number" ? Math.round(v * 100) / 100 : v));
  if (includeAguinaldo) totalRow.push(Math.round((totals.aguinaldo ?? totals.bruto / 12)));
  aoa.push(totalRow);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Anchos (extienden si incluye Aguinaldo)
  const baseWidths = conColRenta(COL_WIDTHS, 12, conRenta);
  const widths = includeAguinaldo ? [...baseWidths, 16] : baseWidths;
  ws["!cols"] = widths.map(w => ({ wch: w }));
  // Alturas: cabecera y headers
  ws["!rows"] = [];
  ws["!rows"][0] = { hpt: 26 };
  ws["!rows"][1] = { hpt: 22 };
  ws["!rows"][2] = { hpt: 18 };
  ws["!rows"][4] = { hpt: 32 };

  // Merges para títulos (filas 0,1,2 across 8 cols)
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 7 } },
  ];

  // Aplicar estilos celda por celda
  const totalCols = headers.length;
  const baseTypes = conColRenta(COL_TYPES, "n", conRenta);
  const colTypes = includeAguinaldo ? [...baseTypes, "n"] : baseTypes;
  for (let r = 0; r < aoa.length; r++) {
    for (let c = 0; c < totalCols; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: "", t: "s" };
      if (r === 0) ws[addr].s = styleTitle;
      else if (r === 1) ws[addr].s = styleSubtitle;
      else if (r === 2) ws[addr].s = styleMetaInfo;
      else if (r === 4) ws[addr].s = styleHeader;
      else if (r === totalRowIdx) {
        const type = colTypes[c];
        ws[addr].s = type === "n" ? styleTotalNum : styleTotal;
      } else if (r > 4) {
        const type = colTypes[c];
        ws[addr].s = type === "n" ? styleCellNum : type === "h" ? styleCellHrs : styleCell;
      }
    }
  }

  ws["!freeze"] = { xSplit: 1, ySplit: 5 };

  return ws;
}

function buildJornadaSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations }) {
  const JORNADAS = ["Diurna", "Mixta", "Nocturna"];
  const aoa = [];
  aoa.push([empresaNombre]);
  aoa.push([`Desglose por jornada — ${tituloPeriodo}`]);
  aoa.push([`Fecha de reporte: ${fechaReporte}`]);
  aoa.push([]);
  const headers = ["Empleado", "Jornada", "Hrs Ord", "Monto Hrs Ord", "Hrs Ext", "Monto Hrs Ext", "Subtotal"];
  aoa.push(headers);

  const data = [];
  for (const { emp, calc } of calculations) {
    for (const j of JORNADAS) {
      const ord = calc.byJornadaOrd[j] || { hrs: 0, monto: 0 };
      const ext = calc.byJornadaExt[j] || { hrs: 0, monto: 0 };
      if ((ord.hrs || 0) + (ext.hrs || 0) === 0) continue;
      data.push([
        `${emp.nombre} ${emp.apellidos || ""}`.trim(),
        j,
        +ord.hrs.toFixed(2), ord.monto,
        +ext.hrs.toFixed(2), ext.monto,
        ord.monto + ext.monto,
      ]);
    }
  }
  for (const row of data) aoa.push(row);

  const totalRowIdx = aoa.length;
  const sumCol = (i) => data.reduce((s, r) => s + (Number(r[i]) || 0), 0);
  aoa.push([
    "TOTAL GENERAL", "",
    +sumCol(2).toFixed(2), sumCol(3),
    +sumCol(4).toFixed(2), sumCol(5),
    sumCol(6),
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 32 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 12 }, { wch: 16 }, { wch: 16 }];
  ws["!rows"] = [];
  ws["!rows"][0] = { hpt: 26 };
  ws["!rows"][1] = { hpt: 22 };
  ws["!rows"][2] = { hpt: 18 };
  ws["!rows"][4] = { hpt: 28 };
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } },
  ];

  const colTypes = ["t", "t", "h", "n", "h", "n", "n"];
  for (let r = 0; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: "", t: "s" };
      if (r === 0) ws[addr].s = styleTitle;
      else if (r === 1) ws[addr].s = styleSubtitle;
      else if (r === 2) ws[addr].s = styleMetaInfo;
      else if (r === 4) ws[addr].s = styleHeader;
      else if (r === totalRowIdx) {
        ws[addr].s = colTypes[c] === "n" ? styleTotalNum : styleTotal;
      } else if (r > 4) {
        const t = colTypes[c];
        ws[addr].s = t === "n" ? styleCellNum : t === "h" ? styleCellHrs : styleCell;
      }
    }
  }

  return ws;
}

const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function buildDetalleSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations, extrasDoblesFeriado, empresa }) {
  const aoa = [];
  aoa.push([empresaNombre]);
  aoa.push([`Detalle por día — ${tituloPeriodo}`]);
  aoa.push([`Fecha de reporte: ${fechaReporte}`]);
  aoa.push([]);
  const headers = ["Empleado", "Fecha", "Día semana", "Estado", "Entrada", "Salida", "Jornada", "Hrs Ord", "Hrs Ext", "Tarifa Ord", "Tarifa Ext", "Monto Ord", "Monto Ext", "Total"];
  aoa.push(headers);

  const DIA_SEMANA = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];
  const dataRowsCount = { val: 0 };
  let totalMontoOrd = 0, totalMontoExt = 0, totalHrsOrd = 0, totalHrsExt = 0;

  for (const { emp, ast } of calculations) {
    if (!ast || ast.length === 0) continue;
    const salarioMensual = Number(emp.salario_base) || 0;
    const ccssDayMap = buildCCSSDayMap(ast);
    // Ordenar por fecha
    const sorted = [...ast].sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
    const hrsList = computeRecordsHours(sorted, emp, ccssDayMap, aplicaJornada48(empresa, emp));
    for (const [idx, r] of sorted.entries()) {
      const h = hrsList[idx];
      const jornadaDia = h.jornada || emp.jornada || "Diurna";
      const tarifaOrd = h.ord > 0 ? horaNormal(salarioMensual, jornadaDia) : 0;
      // Extras del feriado se duplican si extrasDoblesFeriado
      const extMult = h.source === "Feriado" ? (extrasDoblesFeriado ? 2 : 1) : 1;
      const tarifaExt = h.ext > 0 ? horaExtra(salarioMensual, jornadaDia) * extMult : 0;
      // Para feriado, ord se paga doble (1x adicional)
      const ordMult = h.source === "Feriado" ? 2 : 1;
      const montoOrd = Math.round(h.ord * tarifaOrd * ordMult);
      const montoExt = Math.round(h.ext * tarifaExt);
      const total = montoOrd + montoExt;
      totalMontoOrd += montoOrd;
      totalMontoExt += montoExt;
      totalHrsOrd += h.ord;
      totalHrsExt += h.ext;

      const fechaObj = r.fecha ? new Date(r.fecha + "T00:00:00") : null;
      const diaSemana = fechaObj ? DIA_SEMANA[fechaObj.getDay()] : "";
      aoa.push([
        `${emp.nombre} ${emp.apellidos || ""}`.trim(),
        r.fecha || "",
        diaSemana,
        r.estado || "Presente",
        r.entrada || "",
        r.salida || "",
        jornadaDia,
        +h.ord.toFixed(2),
        +h.ext.toFixed(2),
        Math.round(tarifaOrd),
        Math.round(tarifaExt),
        montoOrd,
        montoExt,
        total,
      ]);
      dataRowsCount.val++;
    }
  }

  const totalRowIdx = aoa.length;
  aoa.push([
    "TOTAL GENERAL", "", "", "", "", "", "",
    +totalHrsOrd.toFixed(2), +totalHrsExt.toFixed(2),
    "", "",
    totalMontoOrd, totalMontoExt, totalMontoOrd + totalMontoExt,
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [
    { wch: 28 }, { wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 12 },
    { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
  ];
  ws["!rows"] = [];
  ws["!rows"][0] = { hpt: 26 };
  ws["!rows"][1] = { hpt: 22 };
  ws["!rows"][2] = { hpt: 18 };
  ws["!rows"][4] = { hpt: 28 };
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 13 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 13 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 13 } },
  ];

  const colTypes = ["t","t","t","t","t","t","t","h","h","n","n","n","n","n"];
  for (let r = 0; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: "", t: "s" };
      if (r === 0) ws[addr].s = styleTitle;
      else if (r === 1) ws[addr].s = styleSubtitle;
      else if (r === 2) ws[addr].s = styleMetaInfo;
      else if (r === 4) ws[addr].s = styleHeader;
      else if (r === totalRowIdx) {
        ws[addr].s = colTypes[c] === "n" ? styleTotalNum : styleTotal;
      } else if (r > 4) {
        const t = colTypes[c];
        ws[addr].s = t === "n" ? styleCellNum : t === "h" ? styleCellHrs : styleCell;
      }
    }
  }
  ws["!freeze"] = { xSplit: 1, ySplit: 5 };
  return ws;
}

// ============================================================================
// HOJA: "Reporte de Planillas" (solo en reporte mensual)
// Vista resumen por empleado con las cifras clave. Aplica reglas visuales:
//   - Fila naranja si el empleado NO cotiza CCSS.
//   - Fila amarilla si el empleado es pensionado.
//   - Si el empleado tiene `reportar_medio_tiempo=true`, los montos ganados
//     (salario base, brutos, extras, CCSS, rebajos y neto) se muestran a la
//     mitad. Sólo aplica en ESTA hoja; las demás hojas conservan el monto
//     completo. Los días laborados / incapacidades no se dividen.
// ============================================================================
const styleCellOrange = {
  ...styleCell,
  fill: { fgColor: { rgb: "FBE0CE" } }, // naranja claro para "sin CCSS"
};
const styleCellNumOrange = {
  ...styleCellNum,
  fill: { fgColor: { rgb: "FBE0CE" } },
};
const styleCellYellow = {
  ...styleCell,
  fill: { fgColor: { rgb: "FFF3B8" } }, // amarillo claro para "pensionado"
};
const styleCellNumYellow = {
  ...styleCellNum,
  fill: { fgColor: { rgb: "FFF3B8" } },
};
const styleCellGreen = {
  ...styleCell,
  fill: { fgColor: { rgb: "D2E9D1" } }, // verde claro para "reportar a medio tiempo"
};
const styleCellNumGreen = {
  ...styleCellNum,
  fill: { fgColor: { rgb: "D2E9D1" } },
};

// Etiqueta de leyenda coloreada (una celda por color con texto explicativo).
const styleLegendOrange = {
  font: { name: "Calibri", sz: 10, bold: true, color: { rgb: "8A4A15" } },
  fill: { fgColor: { rgb: "FBE0CE" } },
  alignment: { horizontal: "center", vertical: "center" },
  border: { top: { style: "thin", color: { rgb: "DDD9CD" } }, bottom: { style: "thin", color: { rgb: "DDD9CD" } }, left: { style: "thin", color: { rgb: "DDD9CD" } }, right: { style: "thin", color: { rgb: "DDD9CD" } } },
};
const styleLegendYellow = { ...styleLegendOrange, font: { ...styleLegendOrange.font, color: { rgb: "7A5C15" } }, fill: { fgColor: { rgb: "FFF3B8" } } };
const styleLegendGreen  = { ...styleLegendOrange, font: { ...styleLegendOrange.font, color: { rgb: "355F31" } }, fill: { fgColor: { rgb: "D2E9D1" } } };
const styleLegendLabel = {
  font: { name: "Calibri", sz: 10, bold: true, color: { rgb: "7A6437" } },
  alignment: { horizontal: "right", vertical: "center" },
};

function buildReportePlanillasSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations }) {
  const aoa = [];
  aoa.push([empresaNombre]);
  aoa.push([`Reporte de Planillas — ${tituloPeriodo}`]);
  aoa.push([`Fecha de reporte: ${fechaReporte}`]);
  aoa.push([]);
  // Leyenda de códigos de color (fila 5 = index 4 en aoa)
  aoa.push(["Códigos de color:", "🟠 Sin CCSS", "", "🟡 Pensionado", "", "🟢 Medio tiempo", "", "", "", "", "", ""]);
  aoa.push([]);
  const headers = [
    "Empleado",
    "Días Laborados", "Días Libres", "Días Ausente", "Días Incap. CCSS", "Días Incap. INS",
    "Salario Base", "Salario Bruto sin Extras", "Monto Horas Extras", "Bruto con Extras",
    "Rebajo CCSS", "Rebajos", "Salario Neto",
    "Fecha de salida",
  ];
  aoa.push(headers);

  // Índices de fila donde comienzan/ocurren cosas (para estilizar):
  const LEGEND_ROW = 4;    // "Códigos de color: ..."
  const HEADER_ROW = 6;    // fila de headers
  const DATA_START = 7;    // primera fila de datos

  // Metadatos por fila para aplicar color según flags del empleado.
  const rowMeta = [];
  const totals = { db: 0, dl: 0, da: 0, dc: 0, di: 0, sb: 0, bs: 0, mx: 0, bc: 0, cc: 0, rj: 0, nt: 0 };

  for (const { emp, calc } of calculations) {
    const factor = emp.reportar_medio_tiempo ? 0.5 : 1;
    const salarioBase = (Number(emp.salario_base) || 0) * factor;
    const montoExt = (Number(calc.montoExt) || 0) * factor;
    const brutoConExtras = (Number(calc.totalBruto) || 0) * factor;
    const brutoSinExtras = brutoConExtras - montoExt;
    const ccss = (Number(calc.ccss) || 0) * factor;
    const rebajos = (Number(calc.totalRebajos) || 0) * factor;
    const neto = brutoConExtras - ccss - rebajos;
    const diasLab = Number(calc.diasTrab) || 0;
    const diasLibres = Number(calc.breakdown?.diaLibre?.dias) || 0;
    const diasAusente = Number(calc.breakdown?.ausente?.dias) || 0;
    const diasCCSS = Number(calc.breakdown?.incapacidadCCSS?.dias) || 0;
    const diasINS = Number(calc.breakdown?.incapacidadINS?.dias) || 0;
    // Fecha de salida: sólo se muestra si el empleado quedó Inactivo por
    // despido/renuncia (server marca fecha_salida al aplicar la acción).
    const fechaSalida = (emp.estado === "Inactivo" && emp.fecha_salida) ? emp.fecha_salida : "";

    aoa.push([
      `${emp.nombre} ${emp.apellidos || ""}`.trim(),
      diasLab, diasLibres, diasAusente, diasCCSS, diasINS,
      salarioBase, brutoSinExtras, montoExt, brutoConExtras,
      ccss, rebajos, neto,
      fechaSalida || "—",
    ]);
    // Prioridad: sin CCSS (naranja) > pensionado (amarillo) > medio tiempo (verde) > normal.
    let tint = null;
    if (!emp.ccss_aplica) tint = "orange";
    else if (emp.pensionado) tint = "yellow";
    else if (emp.reportar_medio_tiempo) tint = "green";
    // Comparar Salario Base vs Bruto sin Extras para resaltar el menor en
    // negrita. Si son iguales o alguno es 0, no se resalta ninguno.
    let lowerCol = null;
    if (salarioBase > 0 && brutoSinExtras > 0) {
      if (salarioBase < brutoSinExtras) lowerCol = "base";
      else if (brutoSinExtras < salarioBase) lowerCol = "bruto";
    }
    rowMeta.push({ tint, lowerCol });

    totals.db += diasLab; totals.dl += diasLibres; totals.da += diasAusente; totals.dc += diasCCSS; totals.di += diasINS;
    totals.sb += salarioBase; totals.bs += brutoSinExtras; totals.mx += montoExt;
    totals.bc += brutoConExtras; totals.cc += ccss; totals.rj += rebajos; totals.nt += neto;
  }

  const totalRowIdx = aoa.length;
  aoa.push([
    "TOTAL GENERAL",
    totals.db, totals.dl, totals.da, totals.dc, totals.di,
    totals.sb, totals.bs, totals.mx, totals.bc,
    totals.cc, totals.rj, totals.nt,
    "",
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [
    { wch: 32 },
    { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
    { wch: 14 }, { wch: 20 }, { wch: 16 }, { wch: 16 },
    { wch: 14 }, { wch: 14 }, { wch: 16 },
    { wch: 14 },
  ];
  ws["!rows"] = [];
  ws["!rows"][0] = { hpt: 26 };
  ws["!rows"][1] = { hpt: 22 };
  ws["!rows"][2] = { hpt: 18 };
  ws["!rows"][LEGEND_ROW] = { hpt: 22 };
  ws["!rows"][HEADER_ROW] = { hpt: 32 };
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: headers.length - 1 } },
    // Leyenda: fusionar celdas B–C (naranja), D–E (amarillo), F–G (verde) para
    // que el emoji + texto se lea completo.
    { s: { r: LEGEND_ROW, c: 1 }, e: { r: LEGEND_ROW, c: 2 } },
    { s: { r: LEGEND_ROW, c: 3 }, e: { r: LEGEND_ROW, c: 4 } },
    { s: { r: LEGEND_ROW, c: 5 }, e: { r: LEGEND_ROW, c: 6 } },
  ];

  // Tipos: t=texto, i=entero (días), n=colones
  const colTypes = ["t", "i", "i", "i", "i", "i", "n", "n", "n", "n", "n", "n", "n", "t"];

  for (let r = 0; r < aoa.length; r++) {
    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!ws[addr]) ws[addr] = { v: "", t: c === 0 ? "s" : "n" };
      if (r === 0) ws[addr].s = styleTitle;
      else if (r === 1) ws[addr].s = styleSubtitle;
      else if (r === 2) ws[addr].s = styleMetaInfo;
      else if (r === LEGEND_ROW) {
        // Estilo por columna en la leyenda
        if (c === 0) ws[addr].s = styleLegendLabel;
        else if (c === 1 || c === 2) ws[addr].s = styleLegendOrange;
        else if (c === 3 || c === 4) ws[addr].s = styleLegendYellow;
        else if (c === 5 || c === 6) ws[addr].s = styleLegendGreen;
        else ws[addr].s = { alignment: { horizontal: "left", vertical: "center" } };
      } else if (r === HEADER_ROW) ws[addr].s = styleHeader;
      else if (r === totalRowIdx) {
        ws[addr].s = colTypes[c] === "n" ? styleTotalNum : styleTotal;
        if (colTypes[c] === "i") ws[addr].s = { ...styleTotal, alignment: { horizontal: "right", vertical: "center" } };
      } else if (r > HEADER_ROW) {
        const t = colTypes[c];
        const meta = rowMeta[r - DATA_START];
        const tint = meta?.tint;
        // Bold para la columna con el menor entre Salario Base (c=6) y
        // Bruto sin Extras (c=7) — comparación por empleado.
        const boldLower = (c === 6 && meta?.lowerCol === "base") || (c === 7 && meta?.lowerCol === "bruto");
        if (t === "n") {
          const base = tint === "orange" ? styleCellNumOrange
            : tint === "yellow" ? styleCellNumYellow
            : tint === "green" ? styleCellNumGreen
            : styleCellNum;
          ws[addr].s = boldLower ? { ...base, font: { ...base.font, bold: true } } : base;
        } else if (t === "i") {
          const base = tint === "orange" ? styleCellOrange
            : tint === "yellow" ? styleCellYellow
            : tint === "green" ? styleCellGreen
            : styleCell;
          ws[addr].s = { ...base, alignment: { horizontal: "right", vertical: "center" }, numFmt: "0" };
        } else {
          ws[addr].s = tint === "orange" ? styleCellOrange
            : tint === "yellow" ? styleCellYellow
            : tint === "green" ? styleCellGreen
            : styleCell;
        }
      }
    }
  }
  ws["!freeze"] = { xSplit: 1, ySplit: HEADER_ROW + 1 };
  return ws;
}

// Genera un reporte de planilla.
// kind: "mensual" | "semanal" | "quincenal"
export function generarReportePlanilla({ kind, empresa, calculations, totals, periodoLabel, fileNameLabel, sheetNameLabel }) {
  const empresaNombre = empresa?.razon_social || empresa?.nombre || "Planilla";
  const fechaReporte = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
  const tituloPrefix = kind === "mensual" ? "Planilla Mensual" : kind === "quincenal" ? "Planilla Quincenal" : "Planilla Semanal";
  const tituloPeriodo = `${tituloPrefix} — ${periodoLabel}`;
  const extrasDoblesFeriado = !!empresa?.extras_dobles_feriado;

  const wb = XLSX.utils.book_new();
  const ws1 = buildMainSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations, totals, MONTHS_ES, includeAguinaldo: kind === "mensual", conRenta: empresa?.actividad_grupo === "servicios_jornadas_especiales" && kind !== "semanal" });
  XLSX.utils.book_append_sheet(wb, ws1, sheetNameLabel || "Planilla");
  const ws2 = buildJornadaSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations });
  XLSX.utils.book_append_sheet(wb, ws2, "Desglose por jornada");
  const ws3 = buildDetalleSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations, extrasDoblesFeriado, empresa });
  XLSX.utils.book_append_sheet(wb, ws3, "Detalle por día");

  // Nueva hoja resumen — solo en reporte mensual.
  if (kind === "mensual") {
    const ws4 = buildReportePlanillasSheet({ empresaNombre, tituloPeriodo, fechaReporte, calculations });
    XLSX.utils.book_append_sheet(wb, ws4, "Reporte de Planillas");
  }

  XLSX.writeFile(wb, `${fileNameLabel}.xlsx`);
}
